import { ISettingsProps } from "../Models";
import { StoryTypeOptions } from "../Constants.ts";
import { encodeSpiralSections } from "./spiralSectionsCodec.ts";

/**
 * A link that reopens this worksheet.
 *
 * `path` is the page the link opens on: the landing page the sheet was made
 * on, so whoever receives it lands on "Long Division Worksheets" with its
 * title and copy rather than on the home page. A landing page reads the query
 * back through `settingsForSharedLink`. Left out, the link opens on the home
 * page. RecentWorksheets passes it too, and compares entries by everything
 * after the `?`, so the same sheet made on two pages stays one entry.
 */
export function generateUrl(settings: ISettingsProps, seed?: string, path?: string, present = false) {
    let url = window.location.origin + (path && path !== '/' ? path : '') + '?';
    let operators = settings.Operators?.map((operator) => operator.value);
    operators.forEach((operator) => {
        url += operator + '=1&';
    });
    url += 'v1d=' + settings.NumberRanges[0].NumberOfDigits + '&';
    url += 'v1r=' + settings.NumberRanges[0].NumberRangeSelection.join(',') + '&';
    url += 'v2d=' + settings.NumberRanges[1].NumberOfDigits + '&';
    url += 'v2r=' + settings.NumberRanges[1].NumberRangeSelection.join(',') + '&';
    url += 'ppp=' + settings.NumberOfProblemsPerPage + '&';
    url += 'np=' + settings.NumberOfPages + '&';
    url += 'ap=' + (settings.AnswerPages ? '1' : '0') + '&';
    url += 'wk=' + (settings.WorkedAnswers ? '1' : '0') + '&';
    url += 'cb=' + (settings.Presentation?.carryBoxes ? '1' : '0') + '&';
    url += 'ag=' + (settings.Presentation?.alignmentGrid ? '1' : '0') + '&';
    url += 'lp=' + (settings.Presentation?.largePrint ? '1' : '0') + '&';
    url += 'sr=' + encodeSpiralSections(settings.SpiralSections ?? []) + '&';
    url += 'hp=' + (settings.HalfPage ? '1' : '0') + '&';
    url += 'r=' + (settings.Remainders ? '1' : '0') + '&';
    url += 'td=' + (settings.Timed ? '1' : '0') + '&';
    // The fact fluency level and its ceiling (ADR 0071 §5). 0 means no level.
    url += 'fl=' + (settings.FactLevel ?? 0) + '&';
    url += 'flc=' + (settings.FactLevelCeiling === 12 ? '12' : '10') + '&';
    // Home practice (ADR 0071 §8). Absent for a test, so a link to a level test is unchanged.
    if (settings.FactLevelSheet === 'practice') {
        url += 'flk=p&';
    }
    // Varied stories (ADR 0073). Absent for Simple, so a link to an ordinary sheet is unchanged.
    if (settings.StoryTypes === StoryTypeOptions.Varied) {
        url += 'wst=v&';
    }
    // One key for the format, since a sheet is exactly one of them. Links made
    // before this carried wp/fr/mn instead; ProcessUrl still reads those.
    // Encoded because "Rounding & Comparing" carries an ampersand, which would
    // otherwise split the query string; URLSearchParams decodes it on the way back.
    url += 'fmt=' + encodeURIComponent(settings.Format) + '&';
    url += 'mm=' + settings.MoneyMaxDollars + '&';
    url += 'mq=' + encodeURIComponent(settings.MoneyQuestion) + '&';
    // Coin names are lower-case words; the space in "half dollar" survives a
    // round trip through URLSearchParams, which decodes it on the way back.
    url += 'mcoins=' + encodeURIComponent((settings.MoneyCoins ?? []).join(',')) + '&';
    url += 'dp=' + settings.DecimalPlaces + '&';
    url += 'dw=' + settings.DecimalMaxWhole + '&';
    // "Scientific Notation: Multiply & Divide" (ADR 0130) carries an ampersand, which would split the query
    // string. Only that is escaped, so links to the older types, which have none, read as they always did;
    // URLSearchParams decodes it on the way back.
    url += 'pv=' + String(settings.PlaceValueQuestion).replace(/&/g, '%26') + '&';
    url += 'pvd=' + settings.PlaceValueDigits + '&';
    url += 'rc=' + settings.RoundingComparingQuestion + '&';
    url += 'rcp=' + settings.RoundingPlace + '&';
    url += 'rcd=' + settings.RoundingComparingDigits + '&';
    url += 'rcx=' + settings.RoundingComparingDecimals + '&';
    // Not encoded, matching tdisp below: "A number line" has no `&` to
    // escape, and URLSearchParams round-trips the space either way.
    url += 'rdisp=' + settings.RoundingDisplay + '&';
    url += 'tq=' + settings.TimeQuestion + '&';
    url += 'ti=' + settings.TimeInterval + '&';
    url += 'tdisp=' + settings.TimeDisplay + '&';
    url += 'noon=' + (settings.CrossNoon ? '1' : '0') + '&';
    url += 'oop=' + encodeURIComponent(settings.OrderOfOperationsLevel) + '&';
    url += 'aq=' + encodeURIComponent(settings.AlgebraQuestion) + '&';
    url += 'amv=' + settings.AlgebraMaxValue + '&';
    url += 'neg=' + (settings.AllowNegatives ? '1' : '0') + '&';
    url += 'iq=' + encodeURIComponent(settings.IntegerQuestion) + '&';
    url += 'imv=' + settings.IntegerMaxValue + '&';
    url += 'rk=' + encodeURIComponent(settings.RationalKind) + '&';
    url += 'npq=' + encodeURIComponent(settings.NumberPropertyQuestion) + '&';
    url += 'npm=' + settings.NumberPropertyMaxValue + '&';
    url += 'cvq=' + encodeURIComponent(settings.ConversionQuestion) + '&';
    url += 'ptq=' + encodeURIComponent(settings.PatternQuestion) + '&';
    url += 'ptl=' + settings.PatternLength + '&';
    url += 'rmq=' + encodeURIComponent(settings.RomanQuestion) + '&';
    url += 'rmm=' + settings.RomanMax + '&';
    url += 'msm=' + settings.MagicSquareMax + '&';
    url += 'nlq=' + encodeURIComponent(settings.NumberLineQuestion) + '&';
    url += 'nlr=' + settings.NumberLineRange + '&';
    url += 'pcq=' + encodeURIComponent(settings.PercentQuestion) + '&';
    url += 'pcm=' + settings.PercentMaxValue + '&';
    url += 'rtq=' + encodeURIComponent(settings.RatioQuestion) + '&';
    url += 'rtm=' + settings.RatioMaxValue + '&';
    url += 'gmq=' + encodeURIComponent(settings.GeometryQuestion) + '&';
    url += 'gas=' + settings.GeometryAngleStep + '&';
    url += 'shq=' + encodeURIComponent(settings.ShapeQuestion) + '&';
    url += 'shm=' + settings.ShapeMaxSide + '&';
    url += 'vq=' + encodeURIComponent(settings.VolumeQuestion) + '&';
    url += 'vme=' + settings.VolumeMaxEdge + '&';
    url += 'ciq=' + encodeURIComponent(settings.CircleQuestion) + '&';
    url += 'cir=' + settings.CircleMaxRadius + '&';
    url += 'prq=' + encodeURIComponent(settings.ProbabilityQuestion) + '&';
    url += 'pyq=' + encodeURIComponent(settings.PythagoreanQuestion) + '&';
    url += 'pym=' + settings.PythagoreanMaxSide + '&';
    url += 'avq=' + encodeURIComponent(settings.AverageQuestion) + '&';
    url += 'avl=' + settings.AverageListLength + '&';
    url += 'bgq=' + encodeURIComponent(settings.BarGraphQuestion) + '&';
    url += 'bgm=' + settings.BarGraphMaxValue + '&';
    url += 'bqc=' + settings.BarGraphQuestionsPerChart + '&';
    url += 'lpq=' + encodeURIComponent(settings.LinePlotQuestion) + '&';
    url += 'lps=' + encodeURIComponent(settings.LinePlotScale) + '&';
    url += 'lpm=' + encodeURIComponent(settings.LinePlotMark) + '&';
    url += 'hgq=' + encodeURIComponent(settings.HistogramQuestion) + '&';
    url += 'bpq=' + encodeURIComponent(settings.BoxPlotQuestion) + '&';
    url += 'cpq=' + encodeURIComponent(settings.CoordinateQuestion) + '&';
    url += 'cpg=' + encodeURIComponent(settings.CoordinateGrid) + '&';
    url += 'calq=' + encodeURIComponent(settings.CalendarQuestion) + '&';
    url += 'cals=' + encodeURIComponent(settings.CalendarSpan) + '&';
    url += 'calm=' + encodeURIComponent(settings.CalendarMonth) + '&';
    url += 'ff=' + encodeURIComponent(settings.FactFamilyQuestion) + '&';
    url += 'fq=' + encodeURIComponent(settings.FractionQuestion) + '&';
    url += 'fmd=' + settings.FractionModelMaxDenominator + '&';
    // ADR 0051 §7: Language is worksheet state (printed text), widened to
    // 'fr' alongside 'es'. Anything else - including a value from before this
    // widening - is 'en', which is what every link made before 'fr' existed
    // already meant.
    url += 'lang=' + (settings.Language === 'es' || settings.Language === 'fr' ? settings.Language : 'en') + '&';
    url += 'fd=' + settings.FractionDenominators + '&';
    url += 'md=' + settings.MaxDenominator + '&';
    url += 'ds=' + (settings.DifferentiationSet ? '1' : '0') + '&';
    // The class-set *mode* travels; the roster of student names deliberately
    // does not, and must never be added here - ADR 0011 §1. It is a list of
    // minors and the URL goes into history, the clipboard and Referer headers.
    url += 'cs=' + (settings.ClassSetDistinctProblems ? '1' : '0') + '&';
    url += 'rg=' + settings.Regrouping + '&';
    // Not `mn` - that is the legacy money flag ProcessUrl still reads.
    url += 'mnum=' + settings.MissingNumber;

    // The seed makes the link a worksheet rather than a preset (ADR 0009 §4).
    // Omitted when absent so an old link keeps meaning what it meant.
    if (seed) {
        url += '&s=' + seed;
    }
    // A link that opens on the board rather than on the generator (ADR 0091). It
    // names no setting, so ProcessUrl has nothing to read back.
    if (present) {
        url += '&present=1';
    }

    return url;
}